<template>
  <div
    id="song-player"
    class="h-full"
    :class="activeSeasonContentForSchoolLevel.schoolLevel.slug"
  >
    <transition-fade :duration="500" appear>
      <div
        v-if="routedSong.vocalMix || routedSong.karaokeSoundMix"
        class="karaokePlayer-view flex h-full flex-col"
      >
        <div class="grow-0 shrink-0">
          <span class="title text-[30px] text-almostBlack font-bree">{{
            routedSong.title
          }}</span>
        </div>

        <karaoke-lyrics
          :cuePoints="routedSong?.cuePoints"
          :currentTime="currentTime"
        />

        <ul
          class="karaokePlayer-toolbox flex justify-between w-full grow-0 shrink-0"
        >
          <li class="bg-white rounded-3xl grow-0 shrink basis-[32%]">
            <div
              class="songMetaDetails text-white items-center bg-white rounded-full rounded-bl-none flex justify-between h-[45px] pr-[7px] pl-[15px]"
            >
              <span class="title grow text-[17px] pl-[6px] font-bree">{{
                routedSong.title
              }}</span>
              <span class="duration text-[16px] pr-[11px] font-bree">
                {{
                  (routedSong.vocalMix || routedSong.karaokeSoundMix)
                    ?.durationFormatted
                }}
              </span>
            </div>
            <div class="musicDownload">
              <a
                v-if="routedSong.sheetNotes?.publicUrl"
                :href="routedSong.sheetNotes.publicUrl"
                target="_blank"
                class="text-almostBlack flex text-[17px] justify-between pb-[26px] pt-[22px] pl-[23px] pr-[17px]"
              >
                <span class="download-label--primary">Bladmuziek</span>
                <span
                  class="flex items-center gap-[0.4rem] download-label--secondary text-right"
                >
                  pdf
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100.11 87.6"
                    class="download-icon w-[1.4rem] h-[1.4rem]"
                  >
                    <path
                      d="M6.53 47.51c3.73-.3 7.44 2.48 7.74 6.31.38 4.77-.37 10.06 0 14.84.16 2.11 1.34 3.82 3.39 4.44 21.56.19 43.21.24 64.76-.03 1.57-.55 2.79-1.87 3.15-3.5.28-4.82-.27-9.9.03-14.69.57-9.29 13.01-9.7 14.35-.81-.19 5.14.31 10.54.02 15.66-.55 9.62-8.37 17.35-17.98 17.86H18C8.5 87.15.54 79.39.02 69.85c-.28-5.21.23-10.68 0-15.91.35-3.38 3.1-6.16 6.51-6.43Z"
                    />
                    <path
                      d="M42.63 6.23c.82-8.12 13.8-8.89 14.37 1.18v28.43l6.7-6.64c6.84-5.2 15.28 3.25 10.09 10.09-6.27 6.07-12.25 12.46-18.45 18.58-2.79 2.75-6.26 3.38-9.77 1.46A435.31 435.31 0 0 1 24.74 38.5c-4.03-7.28 4.64-14.46 11.08-8.94 2.36 2.03 4.42 4.74 6.82 6.77V6.23Z"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </li>

          <li
            v-if="routedSong.vocalMix?.publicUrl"
            class="bg-white rounded-3xl grow-0 shrink basis-[32%]"
          >
            <div
              class="audioPlayer-karaoke rounded-full rounded-bl-none flex items-center h-[45px] min-w-[200px] pr-[7px] pl-[15px]"
            >
              <song-player
                :song="routedSong.vocalMix"
                v-model:playing="vocalMixPlayerPlaying"
                @update:currentTime="currentTime = $event"
                v-model:volume="volume"
                v-model:muted="muted"
              />
            </div>

            <div class="musicDownload">
              <a
                :href="routedSong.vocalMix.publicUrl"
                target="_blank"
                class="text-almostBlack flex text-[17px] justify-between pb-[26px] pt-[22px] pl-[23px] pr-[17px]"
              >
                <span class="download-label--primary">Ingezongen</span>
                <span
                  class="flex items-center gap-[0.4rem] download-label--secondary text-right"
                >
                  mp3
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100.11 87.6"
                    class="download-icon w-[1.4rem] h-[1.4rem]"
                  >
                    <path
                      d="M6.53 47.51c3.73-.3 7.44 2.48 7.74 6.31.38 4.77-.37 10.06 0 14.84.16 2.11 1.34 3.82 3.39 4.44 21.56.19 43.21.24 64.76-.03 1.57-.55 2.79-1.87 3.15-3.5.28-4.82-.27-9.9.03-14.69.57-9.29 13.01-9.7 14.35-.81-.19 5.14.31 10.54.02 15.66-.55 9.62-8.37 17.35-17.98 17.86H18C8.5 87.15.54 79.39.02 69.85c-.28-5.21.23-10.68 0-15.91.35-3.38 3.1-6.16 6.51-6.43Z"
                    />
                    <path
                      d="M42.63 6.23c.82-8.12 13.8-8.89 14.37 1.18v28.43l6.7-6.64c6.84-5.2 15.28 3.25 10.09 10.09-6.27 6.07-12.25 12.46-18.45 18.58-2.79 2.75-6.26 3.38-9.77 1.46A435.31 435.31 0 0 1 24.74 38.5c-4.03-7.28 4.64-14.46 11.08-8.94 2.36 2.03 4.42 4.74 6.82 6.77V6.23Z"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </li>

          <li
            v-if="routedSong.karaokeSoundMix?.publicUrl"
            class="bg-white rounded-3xl grow-0 shrink basis-[32%]"
          >
            <div
              class="audioPlayer-karaoke rounded-full rounded-bl-none flex items-center h-[45px] min-w-[200px] pr-[7px] pl-[15px]"
            >
              <song-player
                :song="routedSong.karaokeSoundMix"
                v-model:playing="karaokeSoundMixPlayerPlaying"
                @update:currentTime="currentTime = $event"
                v-model:volume="volume"
                v-model:muted="muted"
                class="text-blue-500"
              />
            </div>
            <div class="musicDownload">
              <a
                :href="routedSong.karaokeSoundMix.publicUrl"
                target="_blank"
                class="text-almostBlack flex text-[17px] justify-between pb-[26px] pt-[22px] pl-[23px] pr-[17px]"
              >
                <span class="download-label--primary">Soundmix</span>
                <span
                  class="flex items-center gap-[0.4rem] download-label--secondary text-right"
                >
                  mp3
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100.11 87.6"
                    class="download-icon w-[1.4rem] h-[1.4rem]"
                  >
                    <path
                      d="M6.53 47.51c3.73-.3 7.44 2.48 7.74 6.31.38 4.77-.37 10.06 0 14.84.16 2.11 1.34 3.82 3.39 4.44 21.56.19 43.21.24 64.76-.03 1.57-.55 2.79-1.87 3.15-3.5.28-4.82-.27-9.9.03-14.69.57-9.29 13.01-9.7 14.35-.81-.19 5.14.31 10.54.02 15.66-.55 9.62-8.37 17.35-17.98 17.86H18C8.5 87.15.54 79.39.02 69.85c-.28-5.21.23-10.68 0-15.91.35-3.38 3.1-6.16 6.51-6.43Z"
                    />
                    <path
                      d="M42.63 6.23c.82-8.12 13.8-8.89 14.37 1.18v28.43l6.7-6.64c6.84-5.2 15.28 3.25 10.09 10.09-6.27 6.07-12.25 12.46-18.45 18.58-2.79 2.75-6.26 3.38-9.77 1.46A435.31 435.31 0 0 1 24.74 38.5c-4.03-7.28 4.64-14.46 11.08-8.94 2.36 2.03 4.42 4.74 6.82 6.77V6.23Z"
                    />
                  </svg>
                </span>
              </a>
            </div>
          </li>
        </ul>
        <svg
          class="pointer-events-none absolute top-0 right-0 translate-x-[-20%] translate-y-[20%]"
          aria-hidden="true"
          width="112px"
          height="112px"
          viewBox="0 0 100.11 100.11"
        >
          <!-- Dynamic colored circle -->
          <path
            :fill="
              activeSeasonContentForSchoolLevel.schoolLevel.slug === 'onderbouw'
                ? '#127b5d'
                : '#6c44a5'
            "
            d="M50.05,100.17C22.41,100.17,0,77.76,0,50.12S22.41.07,50.05.07s50.05,22.41,50.05,50.05-22.41,50.05-50.05,50.05"
          />

          <!-- Static white shape -->
          <path
            fill="#fff"
            d="M33.22,64.61h-.1c-6.11,0-11.08,3.95-11.12,8.84-.03,4.92,4.94,8.96,11.09,9.01h.1
       c6.11,0,11.08-3.95,11.12-8.84,0-.02,0-.03,0-.05,0-.01,0-.02,0-.03v-36.55l22.32-11.16v27.24
       c-1.85-1.13-4.13-1.83-6.63-1.84h-.1
       c-6.1,0-11.08,3.95-11.12,8.84-.03,4.92,4.94,8.96,11.09,9.01h.1
       c6.11,0,11.08-3.95,11.12-8.84,0-.02,0-.03,0-.05,0-.01,0-.02,0-.03V22.22
       c0-.77-.4-1.49-1.06-1.9-.66-.4-1.48-.44-2.17-.1l-26.78,13.39
       c-.76.38-1.23,1.15-1.23,1.99v30.85
       c-1.85-1.13-4.13-1.83-6.63-1.84"
          />
        </svg>
      </div>
    </transition-fade>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import KaraokeLyrics from "../../../components/content/media/music/karaoke/KaraokeLyrics.vue";
import SongPlayer from "../../../components/content/media/music/song/SongPlayer.vue";
import TransitionFade from "../../../components/magic/TransitionFade.vue";

export default {
  name: "KaraokePlayer",
  components: {
    KaraokeLyrics,
    SongPlayer,
    TransitionFade,
  },
  data() {
    return {
      vocalMixPlayerPlaying: false,
      vocalMixPlaybackTracked: false,

      karaokeSoundMixPlayerPlaying: false,
      karaokeSoundMixPlaybackTracked: false,

      currentTime: 0,
      muted: false,
      volume: 75,
    };
  },
  computed: {
    ...mapGetters(["routedSong"]),
    ...mapGetters(["activeSeasonContentForSchoolLevel"]),
  },
  watch: {
    routedSong() {
      // Reset the instance data - except muted & volume.
      this.vocalMixPlayerPlaying = false;
      this.vocalMixPlaybackTracked = false;

      this.karaokeSoundMixPlayerPlaying = false;
      this.karaokeSoundMixPlaybackTracked = false;

      this.currentTime = 0;
    },
    vocalMixPlayerPlaying(playing) {
      if (playing) {
        this.karaokeSoundMixPlayerPlaying = false;
      }
      if (
        !this.vocalMixPlaybackTracked &&
        this.routedSong?.vocalMix?.trackingUrl
      ) {
        // Track song playback.
        navigator.sendBeacon(this.routedSong.vocalMix.trackingUrl);
        this.vocalMixPlaybackTracked = true;
      }
    },
    karaokeSoundMixPlayerPlaying(playing) {
      if (playing) {
        this.vocalMixPlayerPlaying = false;
      }
      if (
        !this.karaokeSoundMixPlaybackTracked &&
        this.routedSong?.karaokeSoundMix?.trackingUrl
      ) {
        // Track karaoke soundmix playback.
        navigator.sendBeacon(this.routedSong.karaokeSoundMix.trackingUrl);
        this.karaokeSoundMixPlaybackTracked = true;
      }
    },
    volume(volume) {
      window.localStorage.setItem("volume", volume);
    },
  },
  mounted() {
    this.applyMemorizedVolume();
  },
  updated() {
    this.applyMemorizedVolume();
  },
  methods: {
    applyMemorizedVolume() {
      this.volume = Number(window.localStorage.getItem("volume") ?? 75);
    },
  },
};
</script>
